import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCompact } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Bloomberg-style always-on status ribbon.
 * Fixed to bottom of viewport, single line of live ticker data.
 */
export default function StatusRibbon() {
    const navigate = useNavigate();
    const { activeOrg } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = () => {
            api.get("/dashboard").then(({ data: d }) => { if (mounted) setData(d?.kpis || null); }).catch(() => {});
        };
        load();
        const t = setInterval(load, 60000); // refresh every minute
        return () => { mounted = false; clearInterval(t); };
    }, [activeOrg?.id]);

    const ccy = activeOrg?.base_currency || "INR";
    const items = [
        { label: "AR",   value: data?.receivables,   route: "/reports/ar-aging",      tone: "warning" },
        { label: "AP",   value: data?.payables,      route: "/reports/ap-aging",      tone: "violet" },
        { label: "CASH", value: data?.cash,          route: "/banking/accounts",      tone: "positive", live: true },
        { label: "GST",  value: data?.gst_liability, route: "/reports/gst-summary",   tone: data?.gst_liability > 0 ? "warning" : "positive" },
        { label: "NET",  value: data?.net_profit,    route: "/reports/profit-loss",   tone: data?.net_profit >= 0 ? "positive" : "destructive" },
    ];

    const fmt = (v) => v == null ? "--" : fmtCompact(v, ccy);
    const tnow = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

    return (
        <div
            className="hidden lg:flex fixed bottom-0 left-0 right-0 h-7 items-center px-4 gap-5 z-30 select-none"
            style={{
                background: "hsl(var(--status-bar))",
                borderTop: "1px solid hsl(var(--status-bar-border))",
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "11px",
            }}
            data-testid="status-ribbon"
        >
            {/* Brand mark */}
            <span className="text-gold tracking-[0.16em] font-medium" style={{ fontSize: "10px" }}>VYA · LIVE</span>
            <span className="live-dot" />

            {/* Org */}
            <span className="text-muted-foreground uppercase tracking-[0.1em] truncate max-w-[220px]" style={{ fontSize: "10px" }}>
                {activeOrg?.name || "—"}
            </span>

            <span className="opacity-30">│</span>

            {/* Tickers */}
            {items.map((it) => (
                <button
                    key={it.label}
                    onClick={() => navigate(it.route)}
                    className="flex items-center gap-1.5 hover:bg-gold/10 px-1.5 -mx-0.5 rounded-sm transition-colors"
                    data-testid={`status-ribbon-${it.label.toLowerCase()}`}
                >
                    <span className="text-muted-foreground tracking-[0.1em]" style={{ fontSize: "9.5px" }}>{it.label}</span>
                    <span className={`tabular-nums font-medium ${
                        it.tone === "positive" ? "text-positive" :
                        it.tone === "destructive" ? "text-destructive" :
                        it.tone === "warning" ? "text-gold" :
                        "text-foreground"
                    }`}>{fmt(it.value)}</span>
                    {it.live && <span className="live-dot" />}
                </button>
            ))}

            <span className="opacity-30">│</span>

            {/* Clock / status */}
            <span className="ml-auto text-muted-foreground tracking-[0.08em] tabular-nums" style={{ fontSize: "10px" }}>
                {tnow} IST
            </span>
        </div>
    );
}
